import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, canvasOf, card, cardName, fade, fill, place, playerColor, sprite, zoneBox,
} from './common';

/**
 * **召喚バンク（軽い）の組み立て**（通常召喚 Issue #578 / 特殊召喚 Issue #573）。
 * `effects/summoning.js` と `effects/spsummoning.js` はこれを呼ぶだけ（指示書 `../direction/summoning.md` / `../direction/spsummoning.md`）。
 *
 * ```
 * 0      画面がわずかに沈む。中央に鋼の板の影がうっすら浮かぶ（溜め。HUD の下）
 * 120    カードが**盤の出たゾーンの位置**から画面の中央へ拡大しながら飛び出す（HUD より手前の層）。後ろに硬い線
 *        （flashy: y 軸に 1 回転して、急停止の直前で正面に戻る）
 * 360    ばんっ！ 中央で急停止（行き過ぎて戻る）。板が実体化、カードの形の閃光 2 フレーム、縁から硬い火花と鋼の破片
 *        → **ヒットストップ 100ms**（火花も止める）。揺れと色収差 1 フレーム
 *        （flashy: 火花・破片が倍、揺れが強い、カードの縁から硬い輪が 2 重に広がる、板の後ろで放射の硬い線が 1 回開く）
 * 460    板の下の段にモンスター名が下から叩き込まれ（着地で小さく揺れ）、SUMMON の札
 * 560    見得（カードはごくゆっくり手前へ）
 * 1100   カードと板が y 軸で回って細くなり消える。名前と SUMMON は横に潰れる。暗さが戻る
 * 1300   破片の最後が落ちて消える
 * ```
 *
 * **盤は触らない**（`hideUntil` / `tweaks` / `places` は使わない）: カードは直前の `move` でもう盤に出ている。
 * ステータス（★・ATK / DEF）は続く `summoned` / `spsummoned` のパネル（`./summonPanel.js`）の仕事なのでここでは出さない。
 * 層は 2 つ: `plate`（HUD の下。暗転・板・硬い線・名前・札）と `card`（**HUD より手前**。飛び出すカード・閃光・火花・破片）。
 * 両方の層が同じ `pose(t)` からカードの位置と大きさを出す。
 *
 * `flashy: false` の数値は通常召喚 v1 のまま（写しただけ）。
 */
const D = 1400;
const DIM_IN = 120;
const FLY = 120;            // カードが盤から飛び出し始める
const HIT = 360;            // 急停止
const STOP = 100;           // ヒットストップ
const SETTLE = 180;         // 行き過ぎから戻る
const NAME_IN = 460;        // 名前が下から
const NAME_HIT = 520;       // 名前の着地
const TAG_HIT = 560;        // SUMMON の着地
const EXIT = 1100;
const EXIT_END = 1300;

const CARD_H = 840;         // 画面の 8 割弱（960 は大きすぎた。ユーザー 2026-09-28）
const CARD_W = CARD_H * CARD_ASPECT;
const CY = 470;             // カードの中心（上 50 / 下 890。上は紋章に掛かり、下の段に名前を置く）
const OVER = 1.06;          // 行き過ぎの大きさ
const OVER_PX = 34;         // 飛び出す向きへの行き過ぎ（px）
const TILT = 0.21;          // 飛んでくる間の y 軸の傾き（rad ≒ 12°）
const PLATE_W = 820;
const PLATE_H = 1060;
const PLATE_CY = 540;
const BAND = 120;           // 板の下の段（名前の帯。名前 84px ぶん）
const NAME_Y = PLATE_CY + PLATE_H / 2 - BAND / 2 - 2;
const TAG_GAP = 18;
const SPARK_LIFE = 0.42;
const SHARD_LIFE = 0.84;

/** 通常 / 少し派手（特殊召喚）の差。**差はカードが出る瞬間（120〜560ms）だけ** */
const LOOK = Object.freeze({
  plain: Object.freeze({ sparks: 30, shards: 10, shake: 26, lines: 16, spin: false, rings: 0, rays: 0 }),
  flashy: Object.freeze({ sparks: 60, shards: 20, shake: 36, lines: 28, spin: true, rings: 2, rays: 12 }),
});
const SPIN_END = HIT - 40;  // 1 回転が終わる（急停止の直前で正面）
const RING_GROW = [0.32, 0.62];   // 輪の広がり（カードの大きさに対する倍率の増え）
const RING_DELAY = [0, 70];       // 2 重目は少し遅れて
const RING_LIFE = 380;            // 止めの後に広がって細くなって消える（ms）
const RAY_OPEN = 60;              // 放射の線が開く（ms）
const RAY_OUT = 200;              // 止めの後に細くなって消える（ms）

/** ヒットストップの時間の写し（叩きつけの後の動きは STOP だけ遅れる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));

/** 2 色の間（'#rrggbb'） */
function mix(a, b, k) {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${pa.map((v, i) => Math.round(v + (pb[i] - v) * k).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * カードの出発点（盤の出たゾーン。舞台のピクセルの中心と、その大きさから出す最初の倍率）。
 * 盤が分からなければプレイヤーの側の画面の外から。
 */
function origin(ctx) {
  const { w: W, h: H } = canvasOf(ctx);
  const { payload } = ctx;
  const a = ctx.anchor?.({ card: payload.uid ?? null, else: { zone: payload.zone ?? null } }) ?? null;
  const box = zoneBox(ctx, payload.zone);
  const screen = a?.screen ?? (box ? [box.cx, box.cy] : null) ?? [W / 2, payload.player === 1 ? -CARD_H * 0.1 : H + CARD_H * 0.1];
  const size = box ? Math.max(box.w, box.h) : 0;
  const s0 = size > 0 ? Math.min(0.5, Math.max(0.06, size / CARD_H)) : 0.12;
  return { x: screen[0], y: screen[1], s0, side: payload.player === 1 ? 1 : -1 };
}

/**
 * **カードの姿勢**（両方の層が同じものを見る）: 位置・倍率・y 軸の傾き。
 * 飛び出し → 行き過ぎで止まる → 止め → back_out で戻る → 見得でゆっくり手前へ → 回って細くなる。
 * `spin` なら飛んでいる間に y 軸に 1 回転（急停止の直前で正面に戻る）。
 */
function pose(t, o, W, spin) {
  const cx = W / 2;
  const k = tween(t, FLY, HIT, 'power3_in');
  const dx = cx - o.x; const dy = CY - o.y;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist; const uy = dy / dist;
  let x = o.x + dx * k; let y = o.y + dy * k;
  let s = o.s0 + (OVER - o.s0) * k;
  let tilt = o.side * TILT * (1 - k);   // 飛んでくる間だけ、召喚したプレイヤーの側へ少し傾く
  if (spin && t < HIT) tilt += -o.side * 2 * Math.PI * tween(t, FLY, SPIN_END, 'power2_out');
  if (t >= HIT) {
    const settle = tween(t, HIT + STOP, HIT + STOP + SETTLE, 'back_out');
    const over = 1 - settle;
    x = cx + ux * OVER_PX * over;
    y = CY + uy * OVER_PX * over;
    s = OVER + (1 - OVER) * settle + 0.03 * seg(t, TAG_HIT, EXIT);
    tilt = 0;
  }
  const exitSpin = (Math.PI / 2) * tween(t, EXIT, EXIT_END, 'power2_in');
  const shown = t >= FLY && t < EXIT_END;
  return { x, y, s, rotY: tilt + exitSpin, shown, a: seg(t, FLY, FLY + 50) };
}

/**
 * **召喚バンクの spec**（`defineEffect` に渡す）。
 *
 * @param {object} o
 * @param {string} o.event `summoning` / `spsummoning`
 * @param {string} o.label
 * @param {string} o.color 帯の色
 * @param {string[]} o.tag 札の文字（1 行 = 1 要素。`['SUMMON']` / `['SPECIAL', 'SUMMON']`）
 * @param {boolean} [o.flashy] カードの出方を少し派手にする（特殊召喚）
 */
export function summonBank({ situation, label, color: bandColor, tag: tagLines, flashy = false }) {
  const look = flashy ? LOOK.flashy : LOOK.plain;
  return {
    situation,
    label,
    color: bandColor,
    opens: ({ payload }) => (payload?.uid || payload?.zone ? payload : null),
    duration: () => D,
    scene: [
      'reset',
      {
        ms: 0,
        grade: {
          // 色収差は急停止の 1 フレームだけ。止めの間はコントラストを上げて硬く
          chroma: { keys: [{ ms: HIT - 1, value: 0 }, { ms: HIT, value: 0.014 }, { ms: HIT + 34, value: 0, ease: 'ease_out' }] },
          contrast: { keys: [{ ms: HIT - 1, value: 1 }, { ms: HIT, value: 1.3 }, { ms: HIT + STOP, value: 1.3, ease: 'hold' }, { ms: HIT + STOP + 1, value: 1 }] },
        },
      },
      { ms: HIT + STOP, shake: { amp: look.shake, freq: 24, decayMs: 380 } },
      { ms: NAME_HIT, shake: { amp: 8, freq: 30, decayMs: 160 } },
    ],
    sounds: () => [
      { at: HIT - 15, name: 'summon' },
      { at: NAME_HIT - 25, name: 'attack', gain: 0.45 },
    ],

    layers: [
      {
        id: 'plate',
        label: `暗転・鋼の板・硬い線・名前・${tagLines.join(' ')}（HUD の下）`,
        space: 'screen',
        script: (ctx) => {
          const { THREE, kit } = ctx;
          const { w: W, h: H } = canvasOf(ctx);
          const p = ctx.payload.player === 1 ? 1 : 0;
          const color = playerColor(ctx, p);
          const o = origin(ctx);
          const root = new THREE.Group();
          const cx = W / 2;

          // --- 沈める地（盤は見えたまま）
          const dim = add(root, place(fill(ctx, '#050507', W, H), W / 2, H / 2), 0);

          // --- 板（溜めの影 → 急停止で実体化）
          const plateTex = kit.steelPlateTexture(PLATE_W, PLATE_H, { cut: 56, band: BAND });
          // 影は暗い地（仮想フィールド）の上でも読めるよう、真っ黒ではなく暗い鋼の色
          const ghost = sprite(ctx, plateTex, PLATE_W, PLATE_H);
          ghost.material.color?.set?.('#2a2d35');
          add(root, ghost, 1);

          // --- 放射の硬い線（flashy。急停止の瞬間に板の後ろで 1 回開いて消える。細い三角）
          const rrand = seeded(`${ctx.seed}:rays`);
          const rays = Array.from({ length: look.rays }, (_, i) => {
            const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
            m.material.color?.set?.(i % 2 === 0 ? '#ffffff' : '#ffd54a');
            add(root, m, 1.5);
            return { m, a: (i / look.rays) * Math.PI * 2 + (rrand() - 0.5) * 0.3, len: 620 + rrand() * 420, w: 16 + rrand() * 14 };
          });

          const plate = add(root, sprite(ctx, plateTex, PLATE_W, PLATE_H), 2);

          // --- 硬い線（ゾーンから中央へ。飛んでくるカードの後ろ）
          const rand = seeded(`${ctx.seed}:lines`);
          const lineCols = ['#ffffff', '#ffd54a', '#e6e9ee', '#ffb020'];
          const lines = [];
          for (let i = 0; i < look.lines; i += 1) {
            const m = fill(ctx, lineCols[i % lineCols.length], 1, 1);
            lines.push({ m, lag: 0.12 + rand() * 0.7, off: (rand() - 0.5) * 1.1, len: 0.12 + rand() * 0.22, w: 2 + rand() * 4 });
            add(root, m, 3);
          }

          // --- 名前と札（板の下の段。1 行に 札 ＋ 名前。札が 2 行なら縦に積む）
          const name = cardName(ctx, ctx.payload.code) || '';
          const word = kit.metalTextTexture(name || ' ', { size: 84, edge: null, italic: false, padding: 20 });   // 少し大きめ（ユーザー 2026-09-28）
          const tags = tagLines.map((s) => kit.metalTextTexture(s, { size: 46, edge: null, italic: false, padding: 20 }));
          const tagW = Math.max(...tags.map((x) => x.width));
          const innerW = PLATE_W - 2 * 24 - 2 * 28;
          const fit = Math.min(1, Math.max(0.35, (innerW - tagW - TAG_GAP) / Math.max(1, word.width)));
          const total = tagW + TAG_GAP + word.width * fit;
          const tagX = cx - total / 2 + tagW / 2;
          const nameX = cx + total / 2 - (word.width * fit) / 2;
          const title = add(root, sprite(ctx, word.texture, word.width, word.height), 5);
          const LINE = 46;   // 札の行の間（46px の文字をすき間なく積む）
          const tagMs = tags.map((x, i) => ({
            m: add(root, sprite(ctx, x.texture, x.width, x.height), 5),
            dy: (i - (tags.length - 1) / 2) * LINE,
          }));
          const underDy = ((tags.length - 1) / 2) * LINE + 30;
          const under = add(root, fill(ctx, color, Math.max(40, tags[tags.length - 1].width - 40), 5), 4);

          return {
            root,
            update: ({ localMs: t }) => {
              const ps = pose(t, o, W, look.spin);
              fade(dim, 0.28 * seg(t, 0, DIM_IN) * (1 - seg(t, EXIT, EXIT_END)));

              // --- 板: 影が少し大きい所から寄り、急停止で本物に。はけるときはカードと同じ向きで細くなる
              const gsc = 1.08 - 0.08 * tween(t, 0, HIT, 'ease_out');
              place(ghost, cx, PLATE_CY, { s: gsc });
              fade(ghost, 0.62 * seg(t, 0, DIM_IN) * (1 - seg(t, HIT - 10, HIT)));
              const punch = t < HIT + STOP ? 1.03 : 1 + 0.03 * (1 - tween(t, HIT + STOP, HIT + STOP + 160, 'back_out'));
              const cosE = Math.cos((Math.PI / 2) * tween(t, EXIT, EXIT_END, 'power2_in'));
              place(plate, cx, PLATE_CY, { sx: punch * Math.max(0.001, cosE), sy: punch });
              fade(plate, t >= HIT && t < EXIT_END - 8 ? 1 : 0);

              // --- 放射の線: 急停止で開き（止めの間は開いたまま）、止めの後に細くなって消える
              const open = tween(t, HIT, HIT + RAY_OPEN, 'expo_out');
              const thin = 1 - tween(t, HIT + STOP, HIT + STOP + RAY_OUT, 'power2_in');
              rays.forEach((r) => {
                const L = r.len * open;
                // CircleGeometry(1, 3) の底辺（x = -0.5）を中心に、先端（x = 1）を外へ
                place(r.m, cx + Math.cos(r.a) * L * 0.5, CY + Math.sin(r.a) * L * 0.5, {
                  sx: Math.max(0.001, L), sy: Math.max(0.001, r.w * thin), rot: r.a,
                });
                fade(r.m, t >= HIT && thin > 0.01 ? 0.9 : 0);
              });

              // --- 硬い線（飛んでいる間だけ。急停止で消える）
              const la = seg(t, FLY + 40, FLY + 100) * (1 - seg(t, HIT, HIT + 40));
              const dx = ps.x - o.x; const dy = ps.y - o.y;
              const dist = Math.hypot(dx, dy);
              const ang = Math.atan2(dy, dx);
              const px = -Math.sin(ang); const py = Math.cos(ang);
              const halfW = (CARD_W * ps.s) / 2;
              lines.forEach((l) => {
                const lx = o.x + dx * l.lag + px * l.off * halfW;
                const ly = o.y + dy * l.lag + py * l.off * halfW;
                l.m.position.set(lx, ly, 0);
                l.m.rotation.z = ang;
                l.m.scale.set(Math.max(0.001, dist * l.len), l.w, 1);
                fade(l.m, la);
              });

              // --- 名前: 下から叩き込まれ（1.5 倍 → 1 倍）、着地で少し沈んで戻る。はけは横に潰れる
              const slam = tween(t, NAME_IN, NAME_HIT, 'power2_in');
              const bounce = t < NAME_HIT ? 1 : 1 - 0.06 * Math.sin(seg(t, NAME_HIT, NAME_HIT + 140) * Math.PI);
              const sq = tween(t, EXIT, EXIT_END, 'power2_in');
              const ns = (1.5 - 0.5 * slam) * bounce * fit;
              place(title, nameX, NAME_Y + 110 * (1 - slam), { sx: ns * Math.max(0.001, 1 - sq), sy: ns });
              fade(title, name && t >= NAME_IN && t < EXIT_END - 8 ? seg(t, NAME_IN, NAME_IN + 30) : 0);

              // --- 札（名前の少し後に、同じく下から）とプレイヤーの色の下線
              const tslam = tween(t, NAME_HIT, TAG_HIT, 'power2_in');
              const tb = t < TAG_HIT ? 1 : 1 - 0.06 * Math.sin(seg(t, TAG_HIT, TAG_HIT + 120) * Math.PI);
              const ts = (1.4 - 0.4 * tslam) * tb;
              const ty = NAME_Y + 4 + 110 * (1 - tslam);
              tagMs.forEach(({ m, dy: ldy }) => {
                place(m, tagX, ty + ldy * ts, { sx: ts * Math.max(0.001, 1 - sq), sy: ts });
                fade(m, t >= NAME_HIT && t < EXIT_END - 8 ? seg(t, NAME_HIT, NAME_HIT + 30) : 0);
              });
              place(under, tagX, ty + underDy, { sx: Math.max(0.001, (1 - sq) * seg(t, TAG_HIT, TAG_HIT + 120)), sy: 1 });
              fade(under, t >= TAG_HIT && t < EXIT_END - 8 ? 1 : 0);
            },
          };
        },
      },
      {
        id: 'card',
        label: '飛び出すカード・閃光・火花・破片（HUD より手前）',
        space: 'screen',
        front: true,
        script: (ctx) => {
          const { THREE, kit } = ctx;
          const { w: W } = canvasOf(ctx);
          const { payload } = ctx;
          const o = origin(ctx);
          const root = new THREE.Group();

          // --- カード 1 枚まるごと（表側で出す。裏側なら持ち主のスリーブ）
          const face = card(ctx, { code: payload.code, player: payload.player, faceUp: payload.position?.faceUp !== false, w: CARD_W });
          add(root, face.mesh, 4);

          // --- カードの形の閃光（通常合成の白。画面全体の加算は使わない）
          const flash = add(root, fill(ctx, '#ffffff', CARD_W * 1.02, CARD_H * 1.02), 6);
          // --- はけの縁（面が縁だけになった瞬間の縦の硬い線）
          const edge = add(root, fill(ctx, '#ffffff', 4, CARD_H), 7);

          // --- 硬い輪（flashy。カードの縁から 2 重に外へ広がって細くなって消える。金 → 白。4 辺の線）
          const rings = Array.from({ length: look.rings }, () => [0, 1, 2, 3].map(() => add(root, fill(ctx, '#ffd54a', 1, 1), 5.5)));

          // --- 火花（カードの縁から放射。金と白の線）と鋼の破片（三角）
          const rand = seeded(`${ctx.seed}:sparks`);
          const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
          /** カードの縁の 1 点とその外向き */
          const onEdge = () => {
            const side = Math.floor(rand() * 4);
            const u = rand();
            const hw = CARD_W / 2; const hh = CARD_H / 2;
            if (side === 0) return { ex: -hw + u * CARD_W, ey: -hh, n: -Math.PI / 2 };
            if (side === 1) return { ex: hw, ey: -hh + u * CARD_H, n: 0 };
            if (side === 2) return { ex: -hw + u * CARD_W, ey: hh, n: Math.PI / 2 };
            return { ex: -hw, ey: -hh + u * CARD_H, n: Math.PI };
          };
          const sparks = [];
          for (let i = 0; i < look.sparks; i += 1) {
            const m = fill(ctx, sparkCols[i % sparkCols.length], 1, 1);
            const e = onEdge();
            sparks.push({ m, ...e, a: e.n + (rand() - 0.5) * 0.9, len: 120 + rand() * 220, w: 3 + rand() * 3, speed: 900 + rand() * 1400 });
            add(root, m, 5);
          }
          const shards = [];
          for (let i = 0; i < look.shards; i += 1) {
            const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
            m.material.color?.set?.(i % 3 === 0 ? '#e6e9ee' : '#9aa1ad');
            const e = onEdge();
            shards.push({ m, ...e, a: e.n + (rand() - 0.5) * 1.2, size: 12 + rand() * 22, stretch: 1.5 + rand() * 1.5, speed: 500 + rand() * 900, spin: (rand() - 0.5) * 28 });
            add(root, m, 5);
          }

          return {
            root,
            update: ({ localMs: t }) => {
              const u = hs(t);
              const ps = pose(t, o, W, look.spin);

              // --- カード（y 軸の傾きは rotation.y。正射影なので幅が細くなる）
              place(face.mesh, ps.x, ps.y, { s: ps.s });
              face.mesh.rotation.y = ps.rotY;
              fade(face.mesh, ps.shown ? ps.a : 0);

              // --- 閃光（急停止の頭の 2 フレームだけ）
              place(flash, ps.x, ps.y, { s: ps.s });
              fade(flash, t >= HIT && t < HIT + 34 ? 0.85 : 0);

              // --- はけの縁の線（細くなりきる瞬間の 1〜2 フレーム）
              place(edge, W / 2, CY, { sx: 1, sy: ps.s });
              fade(edge, t >= EXIT_END - 18 && t < EXIT_END + 18 ? 0.9 : 0);

              // --- 硬い輪（止めの間はカードの縁に張り付いたまま。止めの後に広がって細くなる）
              rings.forEach((bars, i) => {
                const k = seg(u, HIT + RING_DELAY[i], HIT + RING_DELAY[i] + RING_LIFE);
                const e = 1 - (1 - k) * (1 - k);
                const grow = ps.s * (1 + RING_GROW[i] * e);
                const hw = (CARD_W * grow) / 2; const hh = (CARD_H * grow) / 2;
                const th = Math.max(1, 12 * (1 - k));
                const col = mix('#ffd54a', '#ffffff', k);
                const on = t >= HIT + RING_DELAY[i] && k < 1;
                [[ps.x, ps.y - hh, 2 * hw + th, th], [ps.x + hw, ps.y, th, 2 * hh + th],
                  [ps.x, ps.y + hh, 2 * hw + th, th], [ps.x - hw, ps.y, th, 2 * hh + th]].forEach(([bx, by, bw, bh], j) => {
                  place(bars[j], bx, by, { sx: bw, sy: bh });
                  bars[j].material.color?.set?.(col);
                  fade(bars[j], on ? 1 : 0);
                });
              });

              // --- 火花と破片（止めの間はその場で止まる。寿命は帯の終わりより手前）
              const since = Math.max(0, (u - HIT) / 1000);
              sparks.forEach((s) => {
                const k = Math.min(1, since / SPARK_LIFE);
                const r = s.speed * since * (1 - 0.4 * k);
                const len = s.len * (1 - k);
                const mid = r + len / 2;
                s.m.position.set(W / 2 + s.ex + Math.cos(s.a) * mid, CY + s.ey + Math.sin(s.a) * mid, 0);
                s.m.rotation.z = s.a;
                s.m.scale.set(Math.max(0.001, len), s.w, 1);
                fade(s.m, t >= HIT && k < 1 ? 1 : 0);
              });
              shards.forEach((s) => {
                const k = Math.min(1, since / SHARD_LIFE);
                const r = s.speed * since;
                s.m.position.set(W / 2 + s.ex + Math.cos(s.a) * r, CY + s.ey + Math.sin(s.a) * r + 1500 * since * since, 0);
                s.m.rotation.z = s.a + s.spin * since;
                s.m.scale.set(s.size * s.stretch, s.size, 1);
                fade(s.m, t >= HIT && k < 1 ? 1 - k * k : 0);
              });
            },
          };
        },
      },
    ],
  };
}
